/**
 * `AIRealtimeTransportModelRTC.ts` — the WebRTC realtime transport.
 *
 * The second implementation of {@link IRealtimeTransport}; read that file's
 * contract facts first, because every one of them is a way this class can fail
 * silently. It is the SHIPPED transport; the account-wide
 * `webRtcRealtimeTransport` dev setting exists to switch it OFF and fall back
 * to the WebSocket. `AIRealtimeSessionModel.connectAsync` constructs it.
 *
 * Port of Unity's `AIRealtimeTransportModelRTC.cs` in SHAPE — same
 * `oai-events` data channel, same `application/sdp` POST to
 * `/v1/realtime/calls?model=` — but not line for line: that file is unwired by
 * its own session host, it mints an ephemeral key the browser must never hold,
 * and one of its decisions is wrong for the web (see "Disconnect" below).
 *
 * ## What is different from the WebSocket transport, and why it matters
 *
 * Model audio arrives as a **remote `MediaStreamTrack`**, not as
 * `response.output_audio.delta` frames. Only non-audio events ride the data
 * channel. So there is no local PCM queue to observe or flush, and the two
 * members defined in terms of one have to be rebuilt:
 *
 *   - `isAgentSpeaking` is DERIVED (see "Speaking detection").
 *   - `stopOutputAudioPlayback` mutes a `GainNode` and asks the server to drop
 *     what it has already buffered. `response.cancel` alone stops generation
 *     but does not retract audio already in flight.
 *
 * ## Signalling — two routes, minted-key first
 *
 * 1. **Minted key, browser-direct (the normal path).** Campfire's mint service
 *    issues a short-lived client secret (`realtimeKeyMint.ts`), and the SDP
 *    offer goes straight to `https://api.openai.com/v1/realtime/calls?model=…`
 *    with that token. Needs NO proxy, NO server-side OpenAI key and NO prepared
 *    host, and works identically in dev and on a deployed channel — verified
 *    2026-08-28: the mint service reflects any `Origin` (checked for
 *    `localhost:5173` and `a1.campfire3d.com`) and OpenAI answers
 *    `Access-Control-Allow-Origin: *`, and a minted key is accepted there.
 *    This is also Unity's own design.
 * 2. **Same-origin proxy (fallback).** When no minting app key is configured —
 *    prod's `ai-app-config.json` still reads `FILL_ME_IN` — the offer goes to
 *    `{@link K_REALTIME_PROXY_PATH}/v1/realtime/calls`, which nginx and the
 *    Vite dev/preview proxies forward to `api.openai.com` with the bearer key
 *    injected server-side. ⚠ This one DOES need the host to have been prepared.
 *
 * ⚠ Neither route may go through `/__campfire_ai_responses`: that is a fixed
 * rewrite to the campfire AI proxy, and that host INTERCEPTS everything under
 * `/v1/realtime/` — a POST there answers HTTP **200** with its own
 * `{"message":"OpenAI websocket proxy is running"}` index (measured
 * 2026-08-28), so `response.ok` is true and the failure surfaces as an SDP
 * parse error naming WebRTC rather than the edge.
 *
 * ⚠ Browser-direct is safe ONLY with a minted key. The OpenAI API key must
 * never reach a browser — the deploy script aborts if it appears in `dist/`.
 *
 * ## Disconnect
 *
 * ⚠ **Do not copy Unity here.** `AIRealtimeTransportModelRTC.cs` raises its
 * disconnect on ICE `Disconnected` as well as `Failed`/`Closed`. On the web
 * that is wrong: `AIRealtimeSessionModel.wireSession` answers `onDisconnected`
 * by destroying the whole `RealtimeSession`, minting a new `sessionId` and
 * reconnecting — so a three-second wifi blip would wipe the conversation.
 * `"disconnected"` is a RECOVERABLE state, so it gets a grace window and only
 * a failure to recover is reported.
 */

import { Emitter } from "@core/events/emitter";
import { randomUuid } from "@models/utils/uuid";
import type { IRealtimeConversationLog } from "@ai/observability/RealtimeConversationLog";
import { getAiConfig } from "@ai/aiConfig";
import { K_REALTIME_MIC_CONSTRAINTS } from "./realtimeAudio";
import { mintRealtimeEphemeralKeyAsync } from "./realtimeKeyMint";
import type {
  IRealtimeTransport,
  RealtimeMicrophoneReason,
} from "./IRealtimeTransport";

/**
 * Same-origin prefix for the realtime HTTP endpoints, forwarded to
 * `https://api.openai.com` with the key injected server-side.
 *
 * ⚠ Mirrored as a literal in `vite.config.ts` (dev AND preview) and in
 * `deploy/nginx/client-dev.conf`; nginx is deployed against that literal, so
 * renaming it means re-preparing the host. It must also stay OUT of
 * `@core/publicUrl` — base-prefixing it under a named channel points it at a
 * path nginx does not proxy.
 */
export const K_REALTIME_PROXY_PATH = "/__campfire_realtime";

/** OpenAI's realtime call endpoint, used directly when a key can be minted. */
const K_OPENAI_REALTIME_CALLS_URL = "https://api.openai.com/v1/realtime/calls";

/** The data-channel name the Realtime API expects. Not configurable. */
const K_EVENTS_CHANNEL = "oai-events";

/**
 * ICE can sit in `checking` indefinitely with no event, so the connect needs
 * its own deadline — the WebSocket path gets away without one only because a
 * TLS connect fails fast. Without it Spark sits on "connecting" forever and
 * the orchestrator's sticky fallback never gets its failure.
 */
const K_CONNECT_TIMEOUT_MS = 15_000;

/**
 * How long a recoverable ICE `"disconnected"` may last before it is reported
 * as a real disconnect. Erring long is the safe direction: a recovered session
 * keeps its conversation, and a dead one costs only these few seconds before
 * the 4 s reconnect that would have run anyway.
 */
const K_ICE_DISCONNECT_GRACE_MS = 5_000;

/** Unity's `RTC.cs` speaking threshold and hangover, ported. */
const K_SPEAKING_RMS_THRESHOLD = 0.01;
const K_SPEAKING_HANGOVER_MS = 250;
/**
 * Hard ceiling on the post-`stopped` drain. A stuck-TRUE speaking flag is the
 * worst outcome available here — the Stop button never leaves, Spark's crown
 * never goes out, and `waitForResponseIdleAsync` parks until its 60 s timeout,
 * so a workflow script's next `say` starts over the previous line's tail.
 */
const K_SPEAKING_DRAIN_CEILING_MS = 1_500;
/**
 * The analyser poll is FREE-RUNNING for the session's life, deliberately.
 *
 * An event-armed poll cannot be a fallback for the events themselves failing
 * to arrive, which is the case it exists for — `output_audio_buffer.*` are
 * documented but were never observed through this proxy (the SDP exchange
 * could not be completed from Node). Unity polls per frame for the same
 * reason; the web needs an emitter, so this compares and emits only on change.
 */
const K_SPEAKING_POLL_MS = 50;
/**
 * How long the level must stay quiet to clear the flag when NO
 * `output_audio_buffer.stopped` has armed a drain — i.e. the pure fallback.
 *
 * Much longer than {@link K_SPEAKING_HANGOVER_MS} on purpose: with no server
 * signal, a pause between sentences is indistinguishable from the end of the
 * answer, and the analyser only samples ~21 % of the timeline (512 samples
 * every 50 ms), so a short window trips on ordinary speech rhythm.
 */
const K_SPEAKING_UNARMED_QUIET_MS = 1_200;

export class AIRealtimeTransportModelRTC implements IRealtimeTransport {
  readonly onConnected = new Emitter<void>();
  readonly onDisconnected = new Emitter<void>();
  readonly onMessage = new Emitter<string>();
  readonly onError = new Emitter<string>();
  /**
   * Created eagerly at construction — contract fact 4. `wireSession`
   * subscribes to it before `connectAsync` is awaited, so an emitter minted
   * later in `pc.ontrack` would hand that subscription a dead object.
   */
  readonly onAgentSpeakingChanged = new Emitter<boolean>();

  private readonly m_model: string;
  private readonly m_log: IRealtimeConversationLog | null;

  private m_pc: RTCPeerConnection | null = null;
  private m_dc: RTCDataChannel | null = null;
  private m_sender: RTCRtpSender | null = null;
  private m_micStream: MediaStream | null = null;
  private m_micTrack: MediaStreamTrack | null = null;
  private m_micRequested = false;
  private m_muted = true;
  private m_microphoneRecording = false;
  private m_microphoneReason: RealtimeMicrophoneReason = "session_sync";
  private m_disposed = false;
  /** One-shot, so a failed handshake resolves `connectAsync(false)` and does not ALSO schedule a reconnect. */
  private m_wasConnected = false;

  // Output audio graph.
  private m_audioEl: HTMLAudioElement | null = null;
  private m_audioContext: AudioContext | null = null;
  private m_analyser: AnalyserNode | null = null;
  private m_gain: GainNode | null = null;
  private m_analyserBuffer: Float32Array<ArrayBuffer> | null = null;

  // Speaking derivation.
  private m_speaking = false;
  private m_lastLoudAt = 0;
  /**
   * Latched between the WebRTC-only `output_audio_buffer.started` and
   * `.stopped` events. While true, an analyser-level quiet passage is not an
   * end-of-speech signal — the server has explicitly said playback is active.
   */
  private m_outputBufferActive = false;
  private m_bufferStoppedAt = 0;
  private m_speakingPoll: number | null = null;
  /**
   * Set by a barge-in, cleared when output is resumed. Without it the analyser
   * — which sits BEFORE the gain, so it still sees audio after a mute —
   * would re-raise the flag the transport has just forced down.
   */
  private m_speakingSuppressed = false;

  private m_iceGraceTimer: number | null = null;
  /**
   * The connect deadline and the settle callback, held on the INSTANCE rather
   * than as closure locals, because two things outside `connectAsync` need
   * them: `dispose()` must clear the timer (a `stop()` during the SDP window
   * otherwise leaves it alive for up to 15 s holding the peer connection and
   * the `<audio>` element), and `handleTerminal` must be able to SETTLE the
   * connect — ICE can fail before the data channel ever opens, and nothing
   * else would then resolve the promise until the deadline.
   */
  private m_connectTimer: number | null = null;
  private m_finishConnect: ((ok: boolean, code?: string) => void) | null = null;
  /**
   * Aborts the in-flight SDP `fetch`. Without it the deadline bounds only the
   * ICE phase: `fetch` has no default timeout in any browser, and the fallback
   * nginx location allows 120 s, so a stalled upstream would leave
   * `connectAsync` parked long after the transport had been given up on.
   */
  private m_connectAbort: AbortController | null = null;
  /** Whether the SDP exchange has settled — decides which code the deadline reports. */
  private m_sdpSettled = false;
  /**
   * `event_id`s of client events THIS transport sent on its own behalf.
   *
   * An `error` naming one is swallowed rather than forwarded: the orchestrator's
   * model-policy self-heal acts on a bare `invalid_value` whenever a reasoning
   * effort is configured, so an unrelated refusal of our
   * `output_audio_buffer.clear` would silently downgrade the session's
   * reasoning config with nothing connecting the two.
   */
  private readonly m_ownEventIds = new Set<string>();

  constructor(model?: string, log: IRealtimeConversationLog | null = null) {
    this.m_model = model?.trim() || getAiConfig().realtimeModel;
    this.m_log = log;
  }

  get isConnected(): boolean {
    // Contract fact 5: "a client event sent right now will arrive". A
    // peer-connection-level reading makes RealtimeSession's four send guards
    // pass while every send throws InvalidStateError.
    return this.m_dc?.readyState === "open";
  }

  get isAgentSpeaking(): boolean {
    return this.m_speaking;
  }

  get micInputLevel(): number {
    // No consumer in the tree — see `IRealtimeTransport`'s "Members with no
    // consumer". Building a meter nothing renders would cost an extra analyser
    // node per session.
    return 0;
  }

  async connectAsync(): Promise<boolean> {
    if (this.m_pc) return this.isConnected;
    if (typeof RTCPeerConnection === "undefined") {
      this.onError.emit("realtime_webrtc_unsupported");
      return false;
    }

    let settled = false;
    this.m_sdpSettled = false;
    const abort = new AbortController();
    this.m_connectAbort = abort;
    const finish = (ok: boolean, code?: string | undefined): boolean => {
      if (settled) return ok;
      settled = true;
      this.m_finishConnect = null;
      this.clearConnectTimer();
      // Abort and clear together, so the timer and the fetch cannot diverge.
      if (!ok) abort.abort();
      this.m_connectAbort = null;
      if (!ok) {
        if (code) this.onError.emit(code);
        // Release the half-built peer connection: `connectAsync` resolving
        // false leads to `teardownSession`, which calls `dispose()` — but a
        // caller that retries without tearing down would otherwise leak.
        this.releasePeer();
      }
      return ok;
    };

    try {
      const pc = new RTCPeerConnection();
      this.m_pc = pc;

      const ready = new Promise<boolean>((resolve) => {
        // Hoisted so the terminal path can settle the connect too.
        this.m_finishConnect = (ok, code) => resolve(finish(ok, code));
        this.m_connectTimer = window.setTimeout(() => {
          // Name the phase that actually failed: before the SDP exchange has
          // settled, ICE was never reached, and reporting `realtime_ice_failed`
          // would point the console warning and the `session.transport-fallback`
          // note at the wrong subsystem. Both codes take the sticky fallback.
          resolve(
            finish(false, this.m_sdpSettled ? "realtime_ice_failed" : "realtime_sdp_failed"),
          );
        }, K_CONNECT_TIMEOUT_MS);

        // ⚠ The data channel MUST be created before `createOffer()`. SDP is
        // generated from the peer connection's state at offer time; created
        // afterwards the offer carries no `application` m-line, SCTP is never
        // negotiated, `open` never fires — and audio still works, so it looks
        // like a protocol bug rather than an ordering one.
        const dc = pc.createDataChannel(K_EVENTS_CHANNEL);
        this.m_dc = dc;
        // Chrome defaults to "arraybuffer" and Firefox has historically
        // defaulted to "blob"; the session's handler expects a string.
        dc.binaryType = "arraybuffer";
        dc.onopen = () => {
          this.m_wasConnected = true;
          this.onConnected.emit();
          resolve(finish(true));
        };
        dc.onmessage = (e: MessageEvent) => this.handleChannelMessage(e.data);
        dc.onerror = () => {
          if (!settled) resolve(finish(false, "realtime_datachannel_failed"));
        };
        dc.onclose = () => this.handleTerminal();

        pc.ontrack = (e: RTCTrackEvent) => this.attachRemoteAudio(e.streams[0] ?? null);
        pc.onconnectionstatechange = () => this.handleConnectionStateChange();
      });

      // A sendrecv transceiver with a NULL track: the m-line has to exist at
      // offer time, but the microphone is deliberately not requested until the
      // first unmute, so there is no track to add yet. `replaceTrack` fills it
      // in later without renegotiating.
      //
      // (A silent placeholder track sounds better on paper — continuous silent
      // RTP for the server's VAD — but its AudioContext would be constructed
      // here, outside a user gesture, so it would start suspended and produce
      // no frames at all. Identical behaviour, more machinery.)
      const transceiver = pc.addTransceiver("audio", { direction: "sendrecv" });
      this.m_sender = transceiver.sender;

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const exchange = await this.exchangeSdpAsync(offer.sdp ?? "", abort.signal);
      this.m_sdpSettled = true;
      if (exchange.sdp === null) {
        // ⚠ ALWAYS a transport failure, whatever the server called it. An
        // earlier version suppressed this whenever the server had supplied its
        // own `error.code`, to stop the model self-heal and the transport
        // fallback both firing on one failure — but that leaves
        // `m_rejectedTransports` EMPTY for any code the orchestrator does not
        // recognise (`invalid_api_key` from a minted token the server rejects,
        // `insufficient_quota`, a plain 500), and the 4 s reconnect then
        // resolves `webrtc` again, forever. A loop is far worse than an
        // over-eager fallback, and the double-fire is de-duplicated by the
        // orchestrator, which owns both predicates.
        //
        // The server's own code has already gone out verbatim from
        // `exchangeSdpAsync`, so the model self-heal still sees it first.
        return finish(false, "realtime_sdp_failed");
      }
      if (this.m_disposed) return finish(false);

      await pc.setRemoteDescription({ type: "answer", sdp: exchange.sdp });
      return await ready;
    } catch (error) {
      console.warn("[RealtimeRTC] connect failed:", error);
      return finish(false, "realtime_sdp_failed");
    }
  }

  /**
   * POSTs the offer and returns the answer SDP.
   *
   * `reported` says whether the SERVER's own `error.code` was already emitted:
   * a refusal is surfaced VERBATIM so the orchestrator's model-policy self-heal
   * can act on it (a mis-configured `roleModels` entry must fall back to the
   * stock model rather than loop Spark offline), and the caller must then NOT
   * also report a transport failure for the same event.
   */
  private async exchangeSdpAsync(
    sdp: string,
    signal: AbortSignal,
  ): Promise<{ sdp: string | null; reported: boolean }> {
    // Route 1 (see the header): a minted short-lived token lets the browser
    // post directly, with no proxy and no prepared host. Route 2 is the
    // same-origin proxy, whose Authorization is injected server-side — so this
    // never sends the OpenAI key either way.
    const ephemeralKey = await mintRealtimeEphemeralKeyAsync();
    const query = `?model=${encodeURIComponent(this.m_model)}`;
    const url = ephemeralKey
      ? `${K_OPENAI_REALTIME_CALLS_URL}${query}`
      : `${K_REALTIME_PROXY_PATH}/v1/realtime/calls${query}`;
    this.m_log?.note("rtc.sdp-route", ephemeralKey ? "minted-key" : "same-origin-proxy");
    let response: Response;
    try {
      response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/sdp",
          ...(ephemeralKey ? { Authorization: `Bearer ${ephemeralKey}` } : {}),
        },
        body: sdp,
        signal,
      });
    } catch (error) {
      console.warn("[RealtimeRTC] SDP POST failed:", error);
      return { sdp: null, reported: false };
    }

    const contentType = response.headers.get("content-type") ?? "";
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      const code = extractErrorCode(body);
      console.warn(
        `[RealtimeRTC] SDP exchange refused (${response.status} ${code ?? "unknown"}): ` +
          body.slice(0, 300),
      );
      this.m_log?.note("rtc.sdp-refused", `${response.status} ${code ?? ""}`.trim());
      // A model the endpoint refuses must reach the self-heal by its own code;
      // anything else is a transport failure and takes the sticky fallback.
      if (code) this.onError.emit(code);
      return { sdp: null, reported: code !== null };
    }

    const body = await response.text().catch(() => "");

    // ⚠ Diagnose on the BODY, never on the status and never on the content
    // type. Two facts force this, and getting either wrong fails at a 200:
    //
    //   - A host whose nginx has not been re-prepared has no
    //     `/__campfire_realtime/` location, so the request falls through to the
    //     SPA and answers `index.html` at 200. `response.ok` is true, so a
    //     status check would let HTML through and the failure would surface as
    //     an SDP parse error naming WebRTC rather than the edge.
    //   - `api.openai.com/v1/realtime/calls` answers **`text/plain`**, at
    //     **201** (measured 2026-08-28, both `gpt-realtime-2.1` and the generic
    //     `gpt-realtime`). This guard shipped as `contentType.includes(
    //     "application/sdp")` and therefore rejected every SUCCESSFUL exchange
    //     — the first browser run failed with `realtime_sdp_failed` while the
    //     mint, the token and the endpoint were all healthy. Do not put a
    //     content-type test back: the payload's own shape is the only thing
    //     here that is ours to rely on.
    //
    // An SDP answer begins with the version line `v=0`; an HTML fallback page
    // does not. That is the whole discriminator. The content type is still
    // worth REPORTING, because naming `text/html` is what points a reader at
    // the stale-host case rather than at this transport.
    if (!body.trimStart().startsWith("v=0")) {
      console.warn(
        `[RealtimeRTC] SDP endpoint answered ${contentType || "(no content-type)"} ` +
          `that is not an SDP answer` +
          (ephemeralKey
            ? " — unexpected from api.openai.com"
            : ` — this origin is not proxying ${K_REALTIME_PROXY_PATH}/ ` +
              `(run scripts/prepare-client-dev-server.sh, or configure a minting ` +
              `app key so the browser can post directly)`) +
          `: ${body.slice(0, 200)}`,
      );
      this.m_log?.note("rtc.sdp-not-proxied", contentType || "(no content-type)");
      return { sdp: null, reported: false };
    }
    return { sdp: body, reported: false };
  }

  disconnect(): void {
    this.releasePeer();
  }

  sendEvent(json: string): void {
    if (!this.isConnected) return;
    try {
      this.m_dc!.send(json);
    } catch (error) {
      // Contract fact 3 — never throw at the caller. A throw inside
      // `executePendingCall` abandons a `function_call_output` and leaves the
      // model waiting forever.
      console.warn("[RealtimeRTC] send failed:", error);
    }
  }

  setMuted(
    muted: boolean,
    reason: RealtimeMicrophoneReason = "session_sync",
  ): void {
    this.m_muted = muted;
    this.m_microphoneReason = reason;
    if (!muted && !this.m_micRequested) {
      // First unmute: request the microphone NOW, so the browser permission
      // prompt appears only when the mic is used — never on page load or
      // connect. Same deliberate behaviour as the WebSocket transport.
      this.m_micRequested = true;
      void this.attachMicrophoneAsync();
    }
    // Mute is `enabled = false`, never `replaceTrack(null)` and never stopping
    // the sender: those are transport GAPS, and the server's VAD may then never
    // see the user's turn close. A disabled track keeps sending (silence).
    if (this.m_micTrack) {
      this.m_micTrack.enabled = !muted;
      this.setMicrophoneRecording(!muted, reason);
    } else if (muted) {
      this.setMicrophoneRecording(false, reason);
    }
    if (!muted) this.resumeAudioOutputIfNeeded();
  }

  private async attachMicrophoneAsync(): Promise<void> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: K_REALTIME_MIC_CONSTRAINTS,
      });
      if (this.m_disposed) {
        for (const track of stream.getTracks()) track.stop();
        return;
      }
      const track = stream.getAudioTracks()[0] ?? null;
      if (!track || !this.m_sender) {
        for (const t of stream.getTracks()) t.stop();
        this.onError.emit("realtime_mic_attach_failed");
        return;
      }
      this.m_micStream = stream;
      this.m_micTrack = track;
      track.enabled = !this.m_muted;
      await this.m_sender.replaceTrack(track);
      this.setMicrophoneRecording(track.enabled, this.m_microphoneReason);
    } catch (error) {
      // A failed attach is otherwise completely silent: the user speaks, the
      // model hears nothing, and the session looks perfectly healthy.
      console.warn("[RealtimeRTC] microphone attach failed:", error);
      this.m_micRequested = false;
      this.onError.emit("realtime_mic_attach_failed");
    }
  }

  resumeAudioOutputIfNeeded(): void {
    void this.m_audioContext?.resume();
    void this.m_audioEl?.play().catch(() => {
      /* autoplay refusal; the graph still runs through the AudioContext */
    });
    // Deterministic un-silence after a barge-in. Without it one Stop press
    // deafens Spark permanently.
    if (this.m_gain) this.m_gain.gain.value = 1;
    this.m_speakingSuppressed = false;
  }

  stopOutputAudioPlayback(): void {
    if (this.m_gain) this.m_gain.gain.value = 0;
    this.m_speakingSuppressed = true;
    // Ask the server to drop what it has already buffered. `response.cancel`
    // stops it GENERATING; it does not retract audio already in flight, so
    // without this Spark keeps talking over the user after they pressed Stop.
    this.sendOwnEvent({ type: "output_audio_buffer.clear" });
    this.m_outputBufferActive = false;
    this.m_bufferStoppedAt = 0;
    this.m_lastLoudAt = 0;
    // Emit synchronously — this is what releases the crown, the Stop button
    // and `waitForResponseIdleAsync` in one tick.
    this.setSpeaking(false);
  }

  dispose(): void {
    this.m_disposed = true;
    // `releasePeer` clears the connect timer and aborts the in-flight SDP
    // fetch; without that a `stop()` during the SDP window would keep this
    // transport's peer connection and audio element alive to the deadline.
    this.releasePeer();
    this.m_finishConnect = null;
    this.teardownAudioGraph();
  }

  // -- Peer / audio lifecycle -------------------------------------------

  private clearConnectTimer(): void {
    if (this.m_connectTimer === null) return;
    window.clearTimeout(this.m_connectTimer);
    this.m_connectTimer = null;
  }

  private releasePeer(): void {
    if (this.m_iceGraceTimer !== null) {
      window.clearTimeout(this.m_iceGraceTimer);
      this.m_iceGraceTimer = null;
    }
    this.clearConnectTimer();
    this.m_connectAbort?.abort();
    this.m_connectAbort = null;
    try {
      this.m_dc?.close();
    } catch {
      /* already closing */
    }
    this.m_dc = null;
    this.setMicrophoneRecording(false, "session_sync");
    for (const track of this.m_micStream?.getTracks() ?? []) track.stop();
    this.m_micStream = null;
    this.m_micTrack = null;
    this.m_micRequested = false;
    this.m_sender = null;
    try {
      this.m_pc?.close();
    } catch {
      /* already closed */
    }
    this.m_pc = null;
  }

  private setMicrophoneRecording(
    recording: boolean,
    reason: RealtimeMicrophoneReason,
  ): void {
    if (this.m_microphoneRecording === recording) return;
    this.m_microphoneRecording = recording;
    const scope = reason === "push_to_talk" ? "ptt" : "microphone";
    this.m_log?.note(
      `${scope}.recording.${recording ? "started" : "stopped"}`,
      `reason=${reason}, transport=webrtc`,
      { recording, reason, transport: "webrtc" },
    );
  }

  /**
   * ⚠ Runs on every reconnect, not only at shutdown. Chrome caps concurrent
   * `AudioContext`s at around six, so a session that reconnects a handful of
   * times on a flaky link would otherwise go silent several reconnects later,
   * with only a console error attributed to whatever was happening then.
   *
   * A live `srcObject` also keeps the remote track alive after `pc.close()`.
   */
  private teardownAudioGraph(): void {
    if (this.m_speakingPoll !== null) {
      window.clearInterval(this.m_speakingPoll);
      this.m_speakingPoll = null;
    }
    if (this.m_audioEl) {
      this.m_audioEl.pause();
      this.m_audioEl.srcObject = null;
      this.m_audioEl.remove();
      this.m_audioEl = null;
    }
    void this.m_audioContext?.close().catch(() => {
      /* already closed */
    });
    this.m_audioContext = null;
    this.m_analyser = null;
    this.m_gain = null;
    this.m_analyserBuffer = null;
    this.m_outputBufferActive = false;
    this.m_bufferStoppedAt = 0;
    this.m_lastLoudAt = 0;
    this.setSpeaking(false);
  }

  private attachRemoteAudio(stream: MediaStream | null): void {
    if (!stream || this.m_disposed || this.m_audioContext) return;

    // A MUTED <audio> sink. Defensive against a long-standing Chromium
    // behaviour where a peer-connection stream fed to `createMediaStreamSource`
    // yields nothing without a media-element sink attached. It is MUTED because
    // the AudioContext graph below is what the user hears — an unmuted element
    // would double the audio.
    //
    // ⚠ Not confirmed to still reproduce in current Chrome, and it says nothing
    // about visionOS Safari or Quest Browser. Harmless if unnecessary — but do
    // not delete it as unused without measuring, because if it IS still needed
    // its absence kills the analyser and therefore the speaking flag's falling
    // edge, which fails as a stuck Stop button rather than as silence.
    const el = document.createElement("audio");
    el.autoplay = true;
    el.muted = true;
    el.srcObject = stream;
    void el.play().catch(() => {
      /* autoplay refusal — resumeAudioOutputIfNeeded retries from a gesture */
    });
    this.m_audioEl = el;

    const context = new AudioContext();
    const source = context.createMediaStreamSource(stream);
    const analyser = context.createAnalyser();
    analyser.fftSize = 512;
    const gain = context.createGain();
    gain.gain.value = 1;
    // ⚠ Analyser BEFORE the gain. After a barge-in the gain is 0, and an
    // analyser placed after it would read silence — so the drain could never
    // observe the buffer actually emptying and the fallback could not notice
    // anything at all.
    source.connect(analyser);
    analyser.connect(gain);
    gain.connect(context.destination);

    this.m_audioContext = context;
    this.m_analyser = analyser;
    this.m_gain = gain;
    this.m_analyserBuffer = new Float32Array(new ArrayBuffer(analyser.fftSize * 4));
    this.m_speakingPoll = window.setInterval(
      () => this.pollSpeaking(),
      K_SPEAKING_POLL_MS,
    );
  }

  // -- Speaking derivation ----------------------------------------------

  /**
   * Three tiers, because no single source is sufficient:
   *
   *  - RISING edge: `output_audio_buffer.started` (a WebRTC-only server event,
   *    which exists precisely because there are no audio deltas to count).
   *  - FALLING edge: `.stopped` / `.cleared` ARMS a drain; this poll clears the
   *    flag once the level has been quiet for {@link K_SPEAKING_HANGOVER_MS},
   *    with a hard ceiling so a missed quiet window cannot stick it true.
   *  - FALLBACK: this poll is free-running, so it also raises and clears the
   *    flag on level alone if those events never arrive at all.
   */
  private pollSpeaking(): void {
    const analyser = this.m_analyser;
    const buffer = this.m_analyserBuffer;
    if (!analyser || !buffer) return;
    analyser.getFloatTimeDomainData(buffer);
    let sum = 0;
    for (let i = 0; i < buffer.length; i++) sum += buffer[i] * buffer[i];
    const rms = Math.sqrt(sum / buffer.length);
    const now = Date.now();

    // ⚠ The CEILING is evaluated first, and deliberately not behind the "is it
    // loud" branch. A stream that never goes quiet after `stopped` — a noisy
    // remote track, an analyser reading something else — would otherwise never
    // reach it and the flag would stick TRUE forever, which is the single worst
    // outcome here and the thing the ceiling exists to rule out.
    const drainingFor = this.m_bufferStoppedAt > 0 ? now - this.m_bufferStoppedAt : 0;
    if (this.m_speaking && drainingFor >= K_SPEAKING_DRAIN_CEILING_MS) {
      this.setSpeaking(false);
      return;
    }

    if (rms >= K_SPEAKING_RMS_THRESHOLD) {
      this.m_lastLoudAt = now;
      if (!this.m_speakingSuppressed) this.setSpeaking(true);
      return;
    }
    if (!this.m_speaking) return;
    // A `started` event is stronger evidence than one quiet analyser window.
    // Wait for its matching `stopped`/`cleared`; without this latch, an
    // ordinary pause released a workflow's next tool while speech continued.
    if (this.m_outputBufferActive) return;
    const quietFor = now - this.m_lastLoudAt;
    if (this.m_bufferStoppedAt > 0) {
      // ARMED (tier 2): the server said no more audio is coming, so a short
      // quiet window means the buffer has drained.
      if (quietFor >= K_SPEAKING_HANGOVER_MS) this.setSpeaking(false);
      return;
    }
    // UNARMED (tier 3): the events never arrived, so quiet is the ONLY signal —
    // and 250 ms of it is an inter-sentence pause, not the end of an answer.
    // ⚠ Clearing on the short window here made a workflow script's
    // `waitForResponseIdleAsync` settle mid-answer (`response.done` fires when
    // generation completes, while the audio is still draining), so the next
    // `say` started over the previous line's tail — and two `response.create`s
    // on one socket lose the second silently.
    if (quietFor >= K_SPEAKING_UNARMED_QUIET_MS) this.setSpeaking(false);
  }

  private setSpeaking(speaking: boolean): void {
    if (this.m_speaking === speaking) return;
    this.m_speaking = speaking;
    if (!speaking) this.m_bufferStoppedAt = 0;
    this.onAgentSpeakingChanged.emit(speaking);
  }

  // -- Inbound -----------------------------------------------------------

  private handleChannelMessage(data: unknown): void {
    const json = coerceToText(data);
    if (json === null) return;

    // Peel the WebRTC-only audio-buffer lifecycle before forwarding, the same
    // split the WebSocket transport uses for its audio deltas.
    if (json.includes("output_audio_buffer")) {
      try {
        const event = JSON.parse(json) as { type?: string };
        if (event.type === "output_audio_buffer.started") {
          this.m_outputBufferActive = true;
          this.m_bufferStoppedAt = 0;
          this.m_lastLoudAt = Date.now();
          if (!this.m_speakingSuppressed) this.setSpeaking(true);
        } else if (
          event.type === "output_audio_buffer.stopped" ||
          event.type === "output_audio_buffer.cleared"
        ) {
          this.m_outputBufferActive = false;
          this.m_bufferStoppedAt = Date.now();
        }
      } catch {
        // Malformed frames are surfaced by the session's own parse handling.
      }
    }

    // Swallow an error that names one of OUR events — see `m_ownEventIds`.
    if (json.includes('"error"') && this.m_ownEventIds.size > 0) {
      try {
        const event = JSON.parse(json) as {
          type?: string;
          error?: { event_id?: string; code?: string };
        };
        const eventId = event.error?.event_id;
        if (event.type === "error" && eventId && this.m_ownEventIds.has(eventId)) {
          this.m_ownEventIds.delete(eventId);
          console.warn(
            `[RealtimeRTC] server refused a transport-owned event ` +
              `(${event.error?.code ?? "unknown"}) — not forwarded`,
          );
          this.m_log?.note("rtc.own-event-refused", event.error?.code ?? "unknown");
          return;
        }
      } catch {
        /* fall through and forward */
      }
    }

    this.onMessage.emit(json);
  }

  /**
   * Sends a client event on the transport's OWN behalf (not on
   * `RealtimeSession`'s), tagging it so a refusal can be correlated, and
   * logging it — bypassing `RealtimeSession.sendEvent` also bypasses its
   * `logOutbound`, and the Spark feed is SUPPRESSED during a guided workflow
   * run, so a barge-in would otherwise leave no record at all in the one
   * scenario it exists for.
   */
  private sendOwnEvent(event: Record<string, unknown>): void {
    if (!this.isConnected) return;
    const eventId = `cf-rtc-${randomUuid().slice(0, 8)}`;
    this.m_ownEventIds.add(eventId);
    const payload = { ...event, event_id: eventId };
    this.m_log?.logOutbound(payload);
    this.sendEvent(JSON.stringify(payload));
  }

  private handleConnectionStateChange(): void {
    const state = this.m_pc?.connectionState;
    if (state === "connected") {
      if (this.m_iceGraceTimer !== null) {
        window.clearTimeout(this.m_iceGraceTimer);
        this.m_iceGraceTimer = null;
      }
      return;
    }
    if (state === "disconnected") {
      // Recoverable — see the file header. Reporting it immediately (as Unity
      // does) would destroy the conversation over a three-second wifi blip.
      if (this.m_iceGraceTimer !== null) return;
      this.m_iceGraceTimer = window.setTimeout(() => {
        this.m_iceGraceTimer = null;
        if (this.m_pc?.connectionState === "disconnected") this.handleTerminal();
      }, K_ICE_DISCONNECT_GRACE_MS);
      return;
    }
    if (state === "failed" || state === "closed") this.handleTerminal();
  }

  private handleTerminal(): void {
    if (this.m_disposed) return;
    // ⚠ Settle FIRST. ICE can reach `failed` before the data channel ever
    // opens, and `releasePeer()` below closes that channel with no further
    // events — so without this, an in-flight `connectAsync` would sit until its
    // 15 s deadline instead of failing over immediately.
    this.m_finishConnect?.(false, "realtime_ice_failed");
    const wasConnected = this.m_wasConnected;
    this.m_wasConnected = false;
    this.releasePeer();
    this.teardownAudioGraph();
    // Only a session that had actually come up reports a disconnect: a failed
    // handshake resolves `connectAsync(false)` instead, so the session host
    // tears down once rather than also scheduling a reconnect.
    if (wasConnected) this.onDisconnected.emit();
  }
}

/** `{"error":{"code":"invalid_offer",…}}` — shape verified 2026-08-28. */
function extractErrorCode(body: string): string | null {
  try {
    const parsed = JSON.parse(body) as { error?: { code?: unknown } };
    const code = parsed.error?.code;
    return typeof code === "string" && code ? code : null;
  } catch {
    return null;
  }
}

function coerceToText(data: unknown): string | null {
  if (typeof data === "string") return data;
  if (data instanceof ArrayBuffer) return new TextDecoder().decode(data);
  if (ArrayBuffer.isView(data)) {
    return new TextDecoder().decode(data as unknown as Uint8Array);
  }
  // A Blob would need an async read; the channel is set to "arraybuffer" at
  // creation so this should be unreachable.
  return null;
}
